import React from "react";

const glanceRows: [string, string][] = [
  [
    "Children",
    "A parent, legal guardian, clinician, educator, or other authorized adult creates or manages a child profile. Where required, we obtain verifiable parental consent before collecting personal information directly from a child under 13.",
  ],
  [
    "Sensitive information",
    "Developmental, disability, educational, behavioral, health-related, and uploaded-document information may be sensitive. We use it only for stated purposes and with protections appropriate to the context.",
  ],
  [
    "Advertising and sale",
    "We do not sell personal information, share it for cross-context behavioral advertising, or serve targeted advertising based on a child's activity.",
  ],
  [
    "Artificial intelligence",
    "AI may analyze information and generate suggestions or summaries. Outputs can be incomplete or inaccurate and should be reviewed by an adult or professional. We do not use identifiable child information to train general-purpose AI models.",
  ],
  [
    "Choices",
    "Depending on where you live and how you access the Services, you may request access, correction, deletion, restriction, objection, portability, or withdrawal of consent.",
  ],
];

const legalBasisRows: [string, string][] = [
  [
    "Contract",
    "Create and manage accounts; deliver requested features, subscriptions, support, and transactions.",
  ],
  [
    "Legitimate interests",
    "Secure and improve the Services; prevent fraud; understand service use; communicate with organizational customers; and protect legal rights, after considering individuals' rights and expectations.",
  ],
  [
    "Consent",
    "Optional marketing, non-essential cookies, certain research, and processing of sensitive information when consent is the appropriate basis. Consent may be withdrawn prospectively.",
  ],
  [
    "Legal obligation",
    "Comply with tax, accounting, safeguarding, regulatory, court, or lawful government requirements.",
  ],
  [
    "Vital interests or public interest",
    "Protect a person from serious harm or support a task in the public interest when applicable.",
  ],
];

const collectAdultProvides = [
  "Account and contact information, such as name, email address, telephone number, organization, role, username, authentication information, and communication preferences.",
  "Child profile information, such as name or nickname, date or year of birth, age, pronouns or gender where provided, location, school year or grade, interests, routines, strengths, needs, goals, diagnoses, disabilities, developmental history, and support preferences.",
  "Assessment, milestone, goal, activity, and progress information, including responses, observations, attempts, success rates, notes, comments, likes, saved content, parent concerns, strategies, and practitioner or educator input.",
  "Documents and media you choose to upload, such as IEPs, education plans, assessments, reports, forms, images, audio, or video, together with information extracted or generated from them.",
  "AI feature content, including questions, prompts, conversations, feedback, uploaded materials, retrieved sources, and generated responses from features such as Goal Coach, document analysis, recommendations, summaries, and social stories.",
  "Transaction information, such as subscription plan, payment status, billing contact, and transaction identifiers. Payment-card details are generally processed by our payment provider rather than stored by us.",
  "Support, survey, pilot, research, event, contest, and business communications that you choose to provide.",
];

const collectAutomatically = [
  "Device and technical information, including IP address, browser or device type, operating system, app version, language, approximate location derived from IP address, identifiers, and diagnostic data.",
  "Usage information, including sign-in times, pages or screens viewed, features used, searches, clicks, session activity, referring pages, errors, performance, and security events.",
  "Cookie and similar-technology information as described in Section 9.",
];

const usePurposes = [
  "Provide, configure, personalize, and administer accounts, child profiles, subscriptions, assessments, goals, tracking, videos, content, reports, communications, and requested integrations.",
  "Generate recommendations, summaries, analyses, suggested milestones, educational content, and other AI-assisted outputs.",
  "Enable collaboration among authorized parents, caregivers, practitioners, educators, administrators, and organizations.",
  "Authenticate users, manage permissions, secure the Services, prevent misuse and fraud, maintain logs, troubleshoot, and investigate incidents.",
  "Respond to inquiries, provide support, send service messages, collect feedback, and manage pilots or events.",
  "Process subscriptions and transactions and maintain business, tax, and accounting records.",
  "Analyze performance and usage, improve accessibility and usability, develop features, and conduct quality assurance.",
  "Create aggregated or de-identified information that is not reasonably linkable to an individual and use it for analytics, research, service improvement, and reporting, subject to applicable law and contractual limits.",
  "Comply with law, enforce agreements, protect rights and safety, and establish or defend legal claims.",
  "Send marketing communications to adults where permitted. You may unsubscribe from marketing, but necessary service or account messages will continue.",
];

const privacyRights = [
  "request access to or a copy of personal information;",
  "correct inaccurate or incomplete information;",
  "request deletion;",
  "restrict or object to processing, including certain direct marketing;",
  "receive certain information in a portable format;",
  "withdraw consent, without affecting earlier lawful processing;",
  "appeal a refusal of a request where applicable; and",
  "complain to a data-protection authority or regulator.",
];

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10 last:mb-0">
      <h2 className="text-2xl font-bold mb-4">{title}</h2>
      <div className="space-y-4 text-lg leading-relaxed">{children}</div>
    </section>
  );
}

function Subheading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xl font-semibold pt-2">{children}</h3>;
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-6 space-y-2">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function PolicyTable({
  headers,
  rows,
}: {
  headers: [string, string];
  rows: [string, string][];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-base border-collapse">
        <thead>
          <tr className="bg-slate-50">
            <th className="text-left font-semibold border border-slate-200 px-3 py-2 w-1/3">
              {headers[0]}
            </th>
            <th className="text-left font-semibold border border-slate-200 px-3 py-2">
              {headers[1]}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([topic, approach]) => (
            <tr key={topic}>
              <td className="align-top font-medium border border-slate-200 px-3 py-2">
                {topic}
              </td>
              <td className="align-top border border-slate-200 px-3 py-2">
                {approach}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      <div className="max-w-3xl mx-auto py-16 px-4">
        <h1 className="text-4xl font-bold mb-8 text-left">Privacy Policy</h1>
        <div className="bg-white rounded-2xl shadow text-gray-900 text-left p-8">
          <p className="text-sm text-gray-500 mb-2">TinyWins and MiraPlus</p>
          <p className="text-sm text-gray-500 mb-6">Effective Date: September 15, 2026</p>
          <p className="text-sm text-gray-500 mb-6">Last Updated: September 15, 2026</p>

          <div className="text-lg leading-relaxed space-y-4 mb-10">
            <p>
              This Privacy Policy explains how SynchronicityAI Inc. and Therapix
              (each a "Provider" and together, "we," "us," or "our") collect, use,
              disclose, retain, and protect personal information when people use
              TinyWins, MiraPlus, related websites, mobile applications, portals,
              communications, and services that link to this Policy (collectively,
              the "Services").
            </p>
            <p>
              The Provider responsible for personal information depends on how the
              Services are offered. If you subscribe or register directly, the
              Provider identified during registration or payment generally
              determines the purposes and means of processing. If a school,
              clinic, therapist, public authority, or other organization provides
              your account, that organization may control the personal information
              and we may process it on the organization's instructions. Contact
              that organization first for requests concerning an
              organization-managed account.
            </p>
            <p>
              The Services help parents, caregivers, practitioners, educators, and
              authorized organizations support a child's development. They do not
              replace medical advice, diagnosis, treatment, emergency services, or
              the professional judgment of a qualified clinician or educator.
            </p>
          </div>

          <Section title="Privacy at a Glance">
            <PolicyTable headers={["Topic", "Our approach"]} rows={glanceRows} />
          </Section>

          <Section title="1. Scope and Roles">
            <p>
              This Policy applies to personal information processed through the
              Services and related customer support, demonstrations, pilots,
              surveys, events, and business communications. It does not govern
              third-party sites or services that have their own privacy policies.
            </p>
            <p>
              For organization-managed accounts, the customer agreement and
              applicable data-processing terms also apply. If those terms conflict
              with this Policy regarding data we process for the organization, the
              applicable agreement controls to the extent permitted by law.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <Subheading>Information you or an authorized adult provides</Subheading>
            <BulletList items={collectAdultProvides} />

            <Subheading>Information collected automatically</Subheading>
            <BulletList items={collectAutomatically} />

            <Subheading>Information from organizations and other sources</Subheading>
            <p>
              We may receive information from a parent or guardian, school,
              clinic, therapist, practitioner, employer, public authority,
              implementation partner, identity provider, payment provider,
              integration selected by you, or another authorized user. We may
              also receive publicly available business-contact information and
              information needed to detect fraud or protect the Services.
            </p>
          </Section>

          <Section title="3. How We Use Information">
            <p>We use personal information for the following purposes:</p>
            <BulletList items={usePurposes} />
          </Section>

          <Section title="4. Legal Bases for EEA, UK, and Similar Jurisdictions">
            <p>
              Where applicable law requires a legal basis, we rely on one or more
              of the following:
            </p>
            <PolicyTable
              headers={["Legal basis", "Typical purposes"]}
              rows={legalBasisRows}
            />
            <p>
              Health, disability, educational, biometric, or other sensitive
              information is processed only when a lawful condition applies, such
              as explicit consent, health or social-care purposes handled under
              appropriate responsibility, substantial public interest, legal
              claims, or another condition available under applicable law. When
              we act for a school, clinic, or public authority, that organization
              determines the applicable legal basis unless law provides otherwise.
            </p>
          </Section>

          <Section title="5. Artificial Intelligence and Automated Processing">
            <p>
              Some features use machine learning, rules-based systems, retrieval
              tools, document extraction, or generative AI. These tools may use
              profile data, goals, progress entries, uploaded documents, prompts,
              and authorized reference content to produce possible next steps,
              summaries, recommendations, or educational materials.
            </p>
            <p>
              AI outputs are informational and assistive. They may be incomplete,
              inappropriate, or inaccurate and must be reviewed before use.
            </p>
            <p>
              AI outputs are not medical diagnoses, treatment plans, emergency
              guidance, or final eligibility, placement, or education decisions.
            </p>
            <p>
              We do not make decisions that produce legal or similarly significant
              effects about a child solely through automated processing.
            </p>
            <p>
              Authorized personnel may review inputs and outputs for support,
              safety, quality, or incident investigation, subject to access
              controls and confidentiality obligations.
            </p>
            <p>
              We may use service providers to process AI inputs and outputs for
              us. We require them to process information under contractual
              restrictions and applicable law.
            </p>
            <p>
              We do not use identifiable child information to train
              general-purpose AI models. We may use de-identified or aggregated
              information, and adult feedback or content where permitted, to
              evaluate and improve our Services.
            </p>
            <p>
              Do not enter information that is unnecessary for the requested
              feature. If an AI result appears unsafe or incorrect, stop using it
              and consult an appropriate qualified professional.
            </p>
          </Section>

          <Section title="6. How We Disclose Information">
            <p>
              We may disclose personal information only as described below. We do
              not sell personal information or share it for cross-context
              behavioral advertising.
            </p>
            <p>
              <strong>Authorized users and organizations.</strong> We disclose
              information to parents, guardians, practitioners, educators,
              administrators, or organizations according to account permissions
              and customer instructions.
            </p>
            <p>
              <strong>Service providers.</strong> Vendors may support cloud
              hosting, authentication, communications, payments, analytics,
              customer support, security, document extraction, and AI processing.
              They may use information only to provide contracted services or as
              otherwise permitted by law.
            </p>
            <p>
              <strong>Integrations you choose.</strong> When you request an
              integration or direct us to send information to another service,
              that recipient's terms and privacy policy may apply after transfer.
            </p>
            <p>
              <strong>Professional advisers and auditors.</strong> We may disclose
              information to attorneys, accountants, insurers, auditors, and
              similar advisers under appropriate duties of confidentiality.
            </p>
            <p>
              <strong>Legal, safety, and rights protection.</strong> We may
              disclose information if reasonably necessary to comply with law or
              valid legal process, protect a person from harm, investigate fraud
              or security issues, enforce agreements, or protect rights and
              property.
            </p>
            <p>
              <strong>Business transactions.</strong> Information may be reviewed
              or transferred in connection with financing, due diligence,
              reorganization, merger, acquisition, or sale of assets, subject to
              appropriate confidentiality and legal protections.
            </p>
            <p>
              We do not permit third parties to use a child's personal information
              for their own targeted advertising or unrelated marketing.
            </p>
          </Section>

          <Section title="7. Children and Parental Controls">
            <p>
              The Services are designed for use under adult supervision. A parent,
              legal guardian, or authorized organization generally creates and
              controls a child profile. Children should not create an account or
              submit personal information without authorization.
            </p>
            <p>
              When the U.S. Children's Online Privacy Protection Act applies to
              information collected online directly from a child under 13, we
              provide required notice and obtain verifiable parental consent
              before collection, unless a legal exception applies. A parent may
              review the child's personal information, request deletion, refuse
              further collection or use, and revoke consent by contacting us. We
              may verify the requester's identity and authority.
            </p>
            <p>
              A school may authorize processing for an educational purpose where
              law permits the school to act on a parent's behalf. We use
              school-provided information only to deliver the contracted service
              and as allowed by the school's instructions and applicable law.
              Parents and eligible students should direct education-record
              requests to the school when the school controls the record.
            </p>
            <p>
              If we learn that we collected a child's personal information without
              required authorization, we will take reasonable steps to delete it.
              Contact{" "}
              <a href="mailto:info@tinywins.ai" className="text-blue-600 underline">
                info@tinywins.ai
              </a>{" "}
              if you believe this has occurred.
            </p>
          </Section>

          <Section title="8. Educational and Health Related Information">
            <p>
              Information in the Services may relate to an IEP, disability,
              development, behavior, therapy, or health. Whether a particular law
              applies depends on the Provider's role and the customer arrangement.
            </p>
            <p>
              <strong>FERPA.</strong> When we process education records for a
              U.S. educational agency or institution, we do so under the
              applicable agreement and the agency's instructions. The agency
              remains responsible for determining its FERPA basis, providing
              required notices, and responding to parent or eligible-student
              rights unless the agreement says otherwise.
            </p>
            <p>
              <strong>HIPAA.</strong> We are subject to HIPAA only when and to
              the extent we act as a business associate for a covered entity
              under a signed business associate agreement. Information submitted
              directly by a consumer is not automatically protected health
              information under HIPAA, although other privacy laws and this Policy
              may apply.
            </p>
            <p>
              <strong>Clinical records.</strong> We retain records according to
              applicable law, the controlling organization's instructions, and
              our retention schedule. We do not apply a universal seven-year
              period to every record.
            </p>
          </Section>

          <Section title="9. Cookies, Analytics, and Communications">
            <p>
              We use strictly necessary technologies for sign-in, security,
              session management, preferences, and core functionality. Where
              enabled and permitted, we may use analytics technologies to
              understand performance and use. We obtain consent for non-essential
              cookies where required.
            </p>
            <p>
              Browser controls can block or delete cookies, but some features may
              not function. Where a cookie settings tool is available, use it to
              update preferences. We do not use child-profile information for
              targeted advertising. Because legal and technical standards for
              browser-based opt-out signals continue to evolve, we process
              recognized opt-out preference signals where required by applicable
              law.
            </p>
            <p>
              Adults may unsubscribe from promotional email using the link in the
              message or by contacting us. Account, security, billing, and
              service notices are not promotional and may continue while an
              account remains active.
            </p>
          </Section>

          <Section title="10. Data Retention">
            <p>
              We retain personal information only as long as reasonably necessary
              for the purposes described in this Policy, including to provide the
              Services, follow customer instructions, meet legal and contractual
              duties, resolve disputes, maintain security, and enforce agreements.
              Retention depends on the type of information, sensitivity, account
              status, customer contract, legal requirements, and risk of harm from
              continued retention.
            </p>
            <p>
              When an account is deleted or a customer relationship ends, we
              delete or de-identify information according to the applicable
              agreement, retention schedule, and law. Limited information may
              remain in backups until overwritten through the ordinary backup
              cycle or may be retained when necessary for security, fraud
              prevention, legal compliance, or legal claims. De-identified
              information may be retained where it cannot reasonably be linked
              back to an individual.
            </p>
          </Section>

          <Section title="11. Security and Incident Response">
            <p>
              We use administrative, technical, and physical safeguards designed
              to protect personal information. Depending on the service and risk,
              these safeguards may include encryption in transit and at rest,
              identity and access controls, least-privilege permissions, logging,
              monitoring, backups, secure development practices, vendor review,
              and incident-response procedures.
            </p>
            <p>
              No system or transmission is completely secure. Keep credentials
              confidential, use a unique password, sign out of shared devices, and
              notify us promptly of suspected unauthorized access. If a
              personal-data breach occurs, we will investigate, mitigate,
              document, and notify affected parties or authorities when required
              by applicable law or contract.
            </p>
          </Section>

          <Section title="12. International Data Transfers">
            <p>
              We and our service providers may process information in the United
              States, the European Economic Area, the United Kingdom, India, or
              other countries where we or they operate. Those countries may have
              privacy laws different from the laws where you live.
            </p>
            <p>
              For transfers subject to the EEA or UK data-protection laws, we use
              an approved transfer mechanism where required, such as an adequacy
              decision, the European Commission's Standard Contractual Clauses,
              the UK International Data Transfer Agreement or Addendum, or another
              lawful safeguard. You may contact us for information about the
              relevant safeguard. Organization-specific hosting or data-residency
              commitments, if any, are stated in the applicable agreement.
            </p>
          </Section>

          <Section title="13. Your Privacy Rights">
            <p>
              Depending on your location and our role, you may have the right to:
            </p>
            <BulletList items={privacyRights} />
            <p>
              To exercise a right, email{" "}
              <a href="mailto:info@tinywins.ai" className="text-blue-600 underline">
                info@tinywins.ai
              </a>{" "}
              and describe your request. We may verify your identity, authority,
              and relationship to the child. Authorized agents may submit
              requests where law permits, subject to verification. We will not
              discriminate against you for exercising a privacy right. Some rights
              are subject to exceptions, and we may direct organization-managed
              account requests to the controlling school, clinic, or organization.
            </p>
            <p>
              Residents of the EEA or UK may complain to the supervisory
              authority where they live, work, or believe a violation occurred.
              U.S. residents may have additional rights under state law. Because
              we do not sell personal information or use it for cross-context
              behavioral advertising, an opt-out of those activities may not be
              applicable; we will honor legally valid requests and preference
              signals where required.
            </p>
          </Section>

          <Section title="14. Research and Deidentified Information">
            <p>
              We may use aggregated or de-identified information for research,
              statistical analysis, service evaluation, safety, quality
              improvement, and reporting. We take reasonable measures designed to
              prevent re-identification and do not attempt to re-identify
              de-identified information except to test whether de-identification
              controls work or as permitted by law.
            </p>
            <p>
              If a project requires identifiable information beyond providing or
              improving the Services, we will use an appropriate legal basis and
              obtain consent, institutional approval, customer authorization, or
              another permission when required. Publication or external sharing
              will not identify a child without valid authorization.
            </p>
          </Section>

          <Section title="15. Third Party Services and Public Features">
            <p>
              The Services may link to or integrate with third-party websites,
              content, video, payment, screening, or communication services.
              Their privacy practices apply to information they independently
              collect. Review their policies before providing information.
            </p>
            <p>
              If a feature allows an adult to post publicly or share content
              outside a controlled account, the adult is responsible for deciding
              what to disclose. Do not publicly post a child's sensitive
              information. We are not responsible for information an authorized
              user deliberately makes public outside the Services.
            </p>
          </Section>

          <Section title="16. Changes to This Policy">
            <p>
              We may update this Policy to reflect changes in the Services,
              technology, law, or our practices. We will post the revised Policy
              with a new effective date. If changes materially affect how we use
              personal information, we will provide additional notice or obtain
              consent when required.
            </p>
          </Section>

          <Section title="17. Contact Us">
            <p>
              For questions, privacy requests, complaints, or concerns about a
              child's information, contact:
            </p>
            <p>
              SynchronicityAI Inc. and Therapix
              <br />
              Email:{" "}
              <a href="mailto:info@tinywins.ai" className="text-blue-600 underline">
                info@tinywins.ai
              </a>
              <br />
              Websites:{" "}
              <a
                href="https://tinywins.ai"
                className="text-blue-600 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://tinywins.ai
              </a>{" "}
              and{" "}
              <a
                href="https://therapix.io"
                className="text-blue-600 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://therapix.io
              </a>
            </p>
            <p>
              Please include the relevant account email, organization, and enough
              detail to locate the information. Do not send sensitive records by
              unencrypted email. If your account is managed by a school, clinic,
              therapist, or other organization, you may also contact that
              organization directly.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
